// src/components/intake/IntakeLayout.jsx
import React from "react";
import { cn } from "../ui";
import { BRAND } from "../onboarding/onboardingCopy";
import {
  INTRO_BODY,
  INTRO_H2,
  QUESTION_N_OF,
  STEPS_LABEL,
  STEP_DONE,
  STEP_LABELS,
} from "./intakeCopy";

// The question pages' frame (DESIGN.md §Onboarding; proto onboarding/q1.html):
// a 288px side column (brand, the shared intro, the seven-step list with done
// marks from the completed set, decision 21) beside the question column, and
// the fixed action bar (`bar`) at the foot. Under 1024px the side column
// collapses to its brand line. Steps come from the served `pages`, never a
// list of our own (decision 20).
export function IntakeLayout({ pages, completed, current, bar, children }) {
  return (
    <div className="stars-dark tw-min-h-screen tw-bg-bg tw-font-sans tw-text-text lg:tw-flex">
      <aside
        aria-label={STEPS_LABEL}
        className="tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-px-4 tw-py-4 lg:tw-sticky lg:tw-top-0 lg:tw-h-screen lg:tw-w-72 lg:tw-shrink-0 lg:tw-overflow-y-auto lg:tw-border-b-0 lg:tw-border-r lg:tw-px-6 lg:tw-py-8"
      >
        <p className="tw-m-0 tw-text-md tw-font-bold">{BRAND}</p>
        <div className="tw-hidden lg:tw-mt-6 lg:tw-grid lg:tw-gap-6">
          <div>
            <p className="tw-m-0 tw-text-lg tw-font-bold">{INTRO_H2}</p>
            <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-text-muted">
              {INTRO_BODY}
            </p>
          </div>
          <ol className="tw-m-0 tw-grid tw-list-none tw-gap-1 tw-p-0">
            {pages.map((p) => {
              const here = p.key === current;
              const done = !here && completed.includes(p.key);
              return (
                <li
                  key={p.key}
                  aria-current={here ? "step" : undefined}
                  className={cn(
                    "tw-flex tw-min-h-10 tw-items-center tw-gap-3 tw-text-sm tw-font-semibold",
                    here || done ? "tw-text-text" : "tw-text-text-muted",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-pill tw-border tw-border-solid tw-text-xs",
                      here &&
                        "tw-border-transparent tw-bg-action tw-text-action-text",
                      done &&
                        "tw-border-transparent tw-bg-text-accent tw-text-on-accent",
                      !here && !done && "tw-border-border",
                    )}
                  >
                    {done ? "✓" : p.position}
                  </span>
                  {STEP_LABELS[p.key]}
                  {done && <span className="tw-sr-only">{STEP_DONE}</span>}
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
      <main className="tw-min-w-0 tw-flex-1 tw-px-4 tw-pt-6 lg:tw-px-8">
        <div className="tw-max-w-4xl">{children}</div>
        <div aria-hidden="true" className="tw-h-40 lg:tw-h-24" />
      </main>
      {bar}
    </div>
  );
}

// "Question n of 7" and the row of step bars (current in text colour, done in
// the accent).
export function QuestionSteps({ pages, completed, current }) {
  const page = pages.find((p) => p.key === current);
  return (
    <div className="tw-mb-6 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-text-muted">
      <span>{QUESTION_N_OF(page.position, pages.length)}</span>
      <ol
        aria-hidden="true"
        className="tw-m-0 tw-flex tw-list-none tw-gap-1 tw-p-0"
      >
        {pages.map((p) => (
          <li
            key={p.key}
            className={cn(
              "tw-h-1 tw-w-7 tw-rounded-pill",
              p.key === current
                ? "tw-bg-text"
                : completed.includes(p.key)
                  ? "tw-bg-text-accent"
                  : "tw-bg-divider",
            )}
          />
        ))}
      </ol>
    </div>
  );
}

// The action bar (DESIGN.md "Action bar"): fixed to the foot, its contents
// aligned with the question column; under 640px the status line goes first,
// full width.
export function ActionBar({ children }) {
  return (
    <div className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-30 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-bg-bar-bg tw-px-4 tw-py-3 tw-backdrop-blur-md lg:tw-px-8">
      <div className="tw-flex">
        <div
          aria-hidden="true"
          className="tw-hidden tw-shrink-0 lg:tw-block lg:tw-w-64"
        />
        <div className="tw-flex tw-max-w-4xl tw-flex-1 tw-flex-wrap tw-items-center tw-gap-3">
          {children}
        </div>
      </div>
    </div>
  );
}
